// Thumbnail-hero card for `library` and `template` kinds. Integrations
// and plugins use `MarketplaceIntegrationRow` (row layout, logo-only).
// Templates/libraries are visual artifacts and deserve a 16:9 preview;
// integrations are service connectors and read better as rows.
//
// Empty `thumbnail` → `transparentBg` checker so the slot still reads
// as "image goes here". Thumbnails uploaded manually per item.

// Filter logic lives in functions/index.js#matchesFilters — the grid
// children function projects catalog → visible items upstream, so this
// component renders only if the filter passed. No `hide:` predicate
// needed.
export const MarketplaceCard = {
  flow: 'y',
  cursor: 'pointer',
  padding: 'A',
  gap: 'Z',
  borderRadius: 'Z',
  border: '1px solid',
  overflow: 'hidden',
  transition: 'Z defaultBezier border-color, Z defaultBezier transform',
  // A11y + keyboard: tabindex=0 makes the card focusable via Tab;
  // role=button conveys "this does something when pressed" to screen
  // readers. Enter/Space trigger the same handler as onClick (see
  // openDetailFromEl in functions/index.js).
  tabindex: '0',
  role: 'button',
  background: (el) => {
    const host = el.call('marketplaceHostOf', el)
    return host?.selectedItem?.id === el.id ? 'blue.1' : 'dialog'
  },
  borderColor: (el) => {
    const host = el.call('marketplaceHostOf', el)
    return host?.selectedItem?.id === el.id ? 'blue' : 'line'
  },
  ':hover': { borderColor: 'blue' },
  // focus-visible shows the outline on keyboard focus but NOT on
  // mouse click — standard modern a11y pattern.
  ':focus-visible': {
    outline: '2px solid blue',
    outlineOffset: '2px'
  },

  // Grid card click starts a fresh detail-pane journey — clears any
  // lingering `selectedHistory` so the Back button hides. Use
  // `selectItemFresh`; `selectItemWithHistory` is for similar-item
  // clicks inside the detail pane.
  onClick: (e, el) => el.call('openDetailFromEl', el),
  onKeydown: (e, el) => {
    if (e.key !== 'Enter' && e.key !== ' ') return
    e.preventDefault()
    el.call('openDetailFromEl', el)
  },

  // 16:9 hero bleeds into the card's padding so the image is flush with
  // the card edges. Two static children keyed on thumbnail presence —
  // no reactive background-* churn per card. Mirrors DetailHero.
  CardThumb: {
    margin: '-A -A 0',
    aspectRatio: '16 / 9',
    borderBottom: '1px solid line',
    position: 'relative',

    CardThumbImage: {
      hide: (el) => !el.parent.parent.thumbnail,
      width: '100%',
      height: '100%',
      background: (el) => `url("${el.parent.parent.thumbnail}") center / cover no-repeat`,
      backgroundColor: 'dialog',
      __order: ['hide', 'width', 'height', 'background', 'backgroundColor']
    },
    // Transparent-canvas checker. `background: 'transparentBg'` hooks
    // the DS gradient table; longhands shape the 4-layer checker.
    // `__order` keeps the shorthand from wiping longhands (ColorField.js).
    CardThumbPlaceholder: {
      hide: (el) => !!el.parent.parent.thumbnail,
      width: '100%',
      height: '100%',
      background: 'transparentBg',
      backgroundColor: 'document',
      backgroundSize: '20px 20px',
      backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
      backgroundRepeat: 'repeat',
      __order: ['hide', 'width', 'height', 'background', 'backgroundColor', 'backgroundSize', 'backgroundPosition', 'backgroundRepeat']
    },

    // Pin / unpin star, overlaid top-left of the thumbnail so it's
    // always reachable without entering the card. Click stops
    // propagation so it doesn't also open the detail. ★ = pinned,
    // ☆ = not pinned.
    CardPinBtn: {
      extends: 'Button',
      position: 'absolute',
      top: 'Z',
      left: 'Z',
      padding: 'X Y',
      borderRadius: 'Y',
      background: 'black.55',
      color: (el) => el.call('isPinned',
        el.call('marketplaceHostOf', el),
        el.parent.parent.id
      ) ? 'yellow' : 'white',
      fontSize: 'Y',
      backdropFilter: 'blur(4px)',
      text: (el) => el.call('isPinned',
        el.call('marketplaceHostOf', el),
        el.parent.parent.id
      ) ? '★' : '☆',
      title: 'Pin to sidebar',
      ariaLabel: 'Toggle pin',
      onClick: (e, el) => {
        e.stopPropagation()
        const host = el.call('marketplaceHostOf', el)
        el.call('togglePin', host, el.parent.parent.id)
      }
    },

    // Kind badge overlaid on top-right of the thumbnail. Gives each
    // card an at-a-glance kind label without stealing a row in the
    // content area below. `black.55` = DS-token alpha modifier
    // (Color API: dot-notation opacity) — same pattern as AppNavbar's
    // `black.2` hover / LoginWindow's `black.65`.
    CardThumbBadge: {
      tag: 'span',
      position: 'absolute',
      top: 'Z',
      right: 'Z',
      padding: 'X Y',
      borderRadius: 'Y',
      fontSize: 'Y',
      fontWeight: '500',
      background: 'black.55',
      color: 'white',
      textTransform: 'capitalize',
      backdropFilter: 'blur(4px)',
      text: (el) => el.parent.parent.kind,
      // Native HTML `title` attr — browser surfaces the full text on
      // hover. A11y + discoverability for kind filtering.
      title: (el) => `${el.parent.parent.kind} · click card to see details`
    }
  },

  CardTitle: {
    tag: 'h5',
    text: (el) => el.parent.name,
    fontSize: 'Z',
    fontWeight: '600',
    color: 'title',
    margin: '0'
  },

  CardDesc: {
    tag: 'p',
    text: (el) => el.parent.description,
    fontSize: 'Y',
    color: 'caption',
    margin: '0',
    flex: '1'
  },

  CardFooter: {
    flow: 'x',
    align: 'center space-between',
    gap: 'Z',
    marginTop: 'auto',

    CardPrice: {
      tag: 'span',
      text: (el) => el.parent.parent.price > 0 ? `$${el.parent.parent.price}` : 'Free',
      fontSize: 'Y',
      fontWeight: '500',
      color: (el) => el.parent.parent.price > 0 ? 'title' : 'green'
    },

    CardInstall: {
      extends: 'Button',
      text: (el) => {
        const host = el.call('marketplaceHostOf', el)
        return el.call('installButtonText', el.parent.parent, host)
      },
      disabled: (el) => {
        const host = el.call('marketplaceHostOf', el)
        return el.call('installButtonDisabled', el.parent.parent, host)
      },
      onClick: (e, el) => {
        e.stopPropagation()
        const host = el.call('marketplaceHostOf', el)
        return el.call('dispatchInstallAndMark', el.parent.parent, host)
      }
    }
  }
}
